import { DateTime } from "luxon";
import { countBy, values } from "../../../utils/lodash";
import { SystemVariableModel } from "../../../models/SystemVariableModel";
import { VariablesState } from "../../../models/states/VariablesState";
import { PageModel } from "../../../models/PageModel";
import { RespondentModel } from "../../../models/RespondentModel";
import { EPOCH, timeZoneOffset } from "../../../utils/time";
import { QuestionnaireModel } from "../../../models/QuestionnaireModel";
import { NavigationState } from "../../../models/states/NavigationState";
// eslint-disable-next-line import/no-cycle
import { getVariableRow } from "./navigationUtilities";
import { MentionBuilder, MentionBuilderDelegate } from "../../../models/builders/MentionBuilder";
import { MentionModel } from "../../../models/MentionModel";
import { getSettingBoolean } from "../../../models/utilities/settingUtilities";
import { getCoordinates } from "../../../models/utilities/respondentUtilities";

export const getMentions = (state: VariablesState, variableName: string, matrixIndex?: number) => {
    return (state.mentionsByVariableName[variableName.toUpperCase()] || []).filter(
        (mention) => matrixIndex === undefined || mention.matrix === matrixIndex,
    );
};

export const getMentionsCount = (
    state: VariablesState,
    variableName: string,
    matrixIndex?: number,
) => {
    const mentions = getMentions(state, variableName, matrixIndex);
    const count = countBy(mentions, "matrix");
    return values(count).length;
};

export const getMention = (
    state: VariablesState,
    variableName: string,
    matrixIndex: number,
    mentionIndex: number,
) =>
    getMentions(state, variableName, matrixIndex).find((mention) => mention.index === mentionIndex);

export const clearMentions = (
    state: VariablesState,
    variableName: string,
    matrixIndex: number,
    questionnaire: QuestionnaireModel,
) => {
    const varName = variableName.toUpperCase();
    return {
        ...state,
        mentionsByVariableName: {
            ...state.mentionsByVariableName,
            [varName]: (state.mentionsByVariableName[varName] || []).filter(
                (mention) => mention.matrix !== matrixIndex,
            ),
        },
    };
};

export const clearCurrentPageMentions = (
    state: VariablesState,
    variableNames: string[],
    matrixIndex: number,
): VariablesState => {
    const updatedMentionsByVariableName: { [variableName: string]: MentionModel[] } = {};

    // Copy mentions for keys that may not be iterated but need to remain
    Object.entries(state.mentionsByVariableName).forEach(([varName, mentions]) => {
        if (!variableNames.includes(varName)) {
            updatedMentionsByVariableName[varName] = mentions;
        }
    });

    // Iterate over each variable name and its corresponding matrix index
    variableNames.forEach((name) => {
        const varName = name.toUpperCase();
        // Check if mentions exist for the current variable name and if it should be filtered
        if (state.mentionsByVariableName[varName]) {
            // Filter mentions for the current variable name and matrix index
            const filteredMentions = state.mentionsByVariableName[varName].filter(
                (mention) => mention.matrix !== matrixIndex,
            );

            // Update the mentions for the current variable name
            if (filteredMentions.length > 0) {
                updatedMentionsByVariableName[varName] = filteredMentions;
            }
        }
    });

    // Return the updated state with cleared mentions
    return {
        ...state,
        mentionsByVariableName: updatedMentionsByVariableName,
    };
};

const getRow = (currentPage?: PageModel) =>
    currentPage === undefined ? "" : String(currentPage.loopIndex);

export const createSystemVariables = (respondent: RespondentModel, currentPage?: PageModel) => {
    const offset = timeZoneOffset(respondent);
    const t = DateTime.utc();
    const tz = DateTime.local().plus({ minutes: offset });
    const d = tz.diff(respondent.sessionStartTime || DateTime.local());
    return {
        $D: new SystemVariableModel(tz.toFormat("DDDD"), tz.diff(EPOCH, "days").toString()),
        $UTCDATE: new SystemVariableModel(t.toFormat("DDDD"), t.diff(EPOCH, "days").toString()),
        $UTCTIME: new SystemVariableModel(
            t.toFormat("t"),
            (t.second + t.minute * 60 + t.hour * 3600).toString(),
        ),
        $H: new SystemVariableModel(
            tz.toFormat("DDDD"),
            (tz.minute + (tz.hour + 1) * 60).toString(),
        ),
        $J: new SystemVariableModel(tz.toFormat("cccc"), tz.day.toString()),
        $T: new SystemVariableModel(
            `${d.hours.toFixed(0)}:${d.minutes}:${d.seconds}`,
            d.seconds.toFixed(0),
        ),
        $GEOLOCATION: new SystemVariableModel(getCoordinates(respondent)),
        $Z: new SystemVariableModel(tz.toFormat("ZZ")),
        $ROW: new SystemVariableModel(getRow(currentPage)),
        $R: new SystemVariableModel(getRow(currentPage)),
    };
};

export const setVariableMention = (
    state: VariablesState,
    navigation: NavigationState,
    questionnaire: QuestionnaireModel,
    variableName: string,
    index: number,
    builder: MentionBuilderDelegate,
): VariablesState => {
    let newState = { ...state };
    const varName = variableName.toUpperCase();
    const matrixIndex =
        index === undefined ? getVariableRow(navigation, questionnaire, varName) : index;
    // Get existing mentions for the variable before they are removed.
    const mentions = getMentions(newState, varName, matrixIndex);
    // Build the mentionBuilder.
    const setMentions = builder(new MentionBuilder(mentions)).build();
    // Clear and update the state with the new mentions.
    newState = clearMentions(newState, varName, matrixIndex, questionnaire);
    return {
        ...newState,
        mentionsByVariableName: {
            ...newState.mentionsByVariableName,
            [varName]: [
                ...(newState.mentionsByVariableName[varName] || []).filter(
                    (m) => m.matrix !== matrixIndex,
                ),
                ...setMentions.map((mention: MentionModel, i) => {
                    const { value, customChoiceIndex, pastedContent } = mention;
                    let { openEnd } = mention;
                    if (openEnd && !getSettingBoolean(questionnaire, "ALLOW_OPEN_END_LOWER_CASE"))
                        openEnd = openEnd.toUpperCase();
                    return {
                        index: i + 1,
                        matrix: matrixIndex,
                        value,
                        openEnd,
                        customChoiceIndex,
                        pastedContent,
                    };
                }),
            ],
        },
    };
};
